import React, {useEffect, useState} from "react";
import PropTypes from "prop-types";
import { MapContainer, TileLayer, Polygon, Tooltip} from "react-leaflet";
import "leaflet/dist/leaflet.css";
import {currencyFormat} from '../../../utils/helpsers';
import Box from "@mui/material/Box";
// Styles
import {MapViewStyles as styles} from './MapView.styles';

/**
 * Component to render a US Ohio states map using leaflet library.
 * @param {array} data:
 *  - data: Array of values to build the polygon.
 *  - color: The polygon color.
 *  - name: District name.
 * @param {number} districtIrn - Number ID of the reference district
 * @param {number} zoom - The amount of zoom applied on map load.
 * @param {string} height - Define the map height by pixels (example: "400px").
 * @param {string} choroplethVar - The variable machine name to build the choropleth feature.
 * @param {string} employeeScope - The type of visualization of the map (single or family)
 * @returns {JSX.Element}
 */
const MapViewHealth = ({ data, districtIrn, zoom = window.innerWidth <= 1023 ? 6.8 : 7.5, height, choroplethVar, employeeScope}) => {
  const [varRange, setVarRange] = useState();
  const ohioLocation = [40.1, -82.696216];
  const choroplethColors = {
      'null': '#cccccc4D',
      0: '#C3BAEC',
      10: '#B2A6E7',
      20: '#907EDB',
      30: '#7F6BD5',
      40: '#6E57CF',
      50: '#5D44C9',
      60: '#5239C1',
      70: '#4832A8',
      80: '#37267D',
      90: '#1F1646',
      100: '#191237',
  }

  // choroplethVar validation and data process.
    useEffect(() => {
        if (data && choroplethVar) {
            let ranges = { max: null, min: null };
            if (data.length) {
                const ids = data.filter(object => object.variables[0][`${employeeScope}_scope`][choroplethVar] !== null).map(object => {
                    return object.variables[0][`${employeeScope}_scope`][choroplethVar]
                });
                const max = Math.max(...ids);
                const min = Math.min(...ids);
                ranges = { min, max }
            }

            setVarRange(ranges);
        }
    }, [choroplethVar, data, employeeScope]);

    function setChoroplethColor({item, range, key}) {
        let keyColor = item[key] === null
            ? 'null'
            :  Math.floor(((item[choroplethVar] - range?.min) / (range?.max - range?.min))*10)*10
        return choroplethColors[keyColor]
    }

  return (
      <Box className="MapViewer" sx={styles}>
        <MapContainer attributionControl={false} center={ohioLocation} zoom={zoom} scrollWheelZoom={false} zoomControl={true} zoomSnap={0.25} zoomDelta={.25} style={{height: height}}>
          <TileLayer
              attribution='&amp;copy <a href="http://osm.org/copyright">OpenStreetMap</a> contributors'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          {
            data.map((district, index) =>
                district.variables[0].data && (
                    <Polygon key={index} positions={district.variables[0].data[0]}
                             color={district.irn === districtIrn ? "#FFC300" : "#f7f5e54f"}
                             pathOptions={{
                                 fillColor:
                                     district.irn === districtIrn
                                         ? "#FFC300"
                                         : !choroplethVar
                                             ? district.color ?? "#4646FF"
                                             : setChoroplethColor({
                                                 item: district.variables[0][`${employeeScope}_scope`],
                                                 range: varRange,
                                                 key: choroplethVar,
                                                 choroplethColors: choroplethColors
                                             })
                              }}
                             fillOpacity={.9}
                             weight={1}>
                      <Tooltip>
                        <div>{district.name}</div>
                        <div>Physical Address: <strong>{district.variables[0].physical_address ? district.variables[0].physical_address: "null"}</strong></div>
                            {  employeeScope === 'family' && (
                                <>
                                    <div className={'choropleth-var'}>
                                    Percentage Of BA Salary Family: <strong>{district.variables[0]['family_scope'].percentage_of_ba_salary_family ? currencyFormat(district.variables[0]['family_scope'].percentage_of_ba_salary_family) : "null"}</strong>
                                    </div>
                                    <div className={'choropleth-var'}>
                                        Deductible in Network Family: <strong>{district.variables[0]['family_scope'].q19c_netdedf_p9q2r2m1c1 ? currencyFormat(district.variables[0]['family_scope'].q19c_netdedf_p9q2r2m1c1) : "null"}</strong>
                                    </div>
                                    <div className={'choropleth-var'}>
                                        District Premium Contribution Family: <strong>{district.variables[0]['family_scope'].q23m_erpremf_p9q6r5m1c1 ? currencyFormat(district.variables[0]['family_scope'].q23m_erpremf_p9q6r5m1c1) : "null"}</strong>
                                    </div>
                                    <div className={'choropleth-var'}>
                                        Employee Contribution of Premium Family: <strong>{district.variables[0]['family_scope'].employee_contribution_pct_family + "%" ?? "null"}</strong>
                                    </div>
                                    <div className={'choropleth-var'}>
                                        Employee Premium Contribution Family: <strong>{district.variables[0]['family_scope'].q23n_eepremf_p9q6r5m1c2 ? currencyFormat(district.variables[0]['family_scope'].q23n_eepremf_p9q6r5m1c2) : "null"}</strong>
                                    </div>
                                    <div className={'choropleth-var'}>
                                        Total Out of Pocket Family: <strong>{district.variables[0]['family_scope'].q20c_netoopf_p9q3r2m1c1 ? currencyFormat(district.variables[0]['family_scope'].q20c_netoopf_p9q3r2m1c1) : "null"}</strong>
                                    </div>
                                    <div className={'choropleth-var'}>
                                        Total Premium Family: <strong>{district.variables[0]['family_scope'].q23o_totpremf_p9q6r5m1c3 ? currencyFormat(district.variables[0]['family_scope'].q23o_totpremf_p9q6r5m1c3) : "null"}</strong>
                                    </div>
                                </>
                            )}
                            {   employeeScope === 'single' && (
                                <>
                                    <div className={'choropleth-var'}>
                                        Percentage Of BA Salary Single: <strong>{district.variables[0]['single_scope'].percentage_of_ba_salary_single ? currencyFormat(district.variables[0]['single_scope'].percentage_of_ba_salary_single) : "null"}</strong>
                                    </div>
                                    <div className={'choropleth-var'}>
                                        Deductible in Network Single: <strong>{district.variables[0]['single_scope'].q19a_netdeds_p9q2r1m1c1 ? currencyFormat(district.variables[0]['single_scope'].q19a_netdeds_p9q2r1m1c1) : "null"}</strong>
                                    </div>
                                    <div className={'choropleth-var'}>
                                        District Premium Contribution Single: <strong>{district.variables[0]['single_scope'].q23a_erprems_p9q6r1m1c1 ? currencyFormat(district.variables[0]['single_scope'].q23a_erprems_p9q6r1m1c1) : "null"}</strong>
                                    </div>
                                    <div className={'choropleth-var'}>
                                        Employee Contribution of Premium Single: <strong>{district.variables[0]['single_scope'].employee_contribution_pct_single + "%" ?? "null"}</strong>
                                    </div>
                                    <div className={'choropleth-var'}>
                                        Employee Premium Contribution Single: <strong>{district.variables[0]['single_scope'].q23b_eeprems_p9q6r1m1c2 ? currencyFormat(district.variables[0]['single_scope'].q23b_eeprems_p9q6r1m1c2) : "null"}</strong>
                                    </div>
                                    <div className={'choropleth-var'}>
                                        Total Out of Pocket Single: <strong>{district.variables[0]['single_scope'].q20a_netoops_p9q3r1m1c1 ? currencyFormat(district.variables[0]['single_scope'].q20a_netoops_p9q3r1m1c1) : "null"}</strong>
                                    </div>
                                    <div className={'choropleth-var'}>
                                        Total Premium Single: <strong>{district.variables[0]['single_scope'].q23c_totprems_p9q6r1m1c3 ? currencyFormat(district.variables[0]['single_scope'].q23c_totprems_p9q6r1m1c3) : "null"}</strong>
                                    </div>
                                </>
                            )}
                      </Tooltip>
                    </Polygon>
                )
            )}
        </MapContainer>
      </Box>
  );
};

MapViewHealth.propTypes = {
    data: PropTypes.array.isRequired,
    districtIrn: PropTypes.number,
    zoom: PropTypes.number,
    height: PropTypes.string,
    choroplethVar: PropTypes.string,
    type: PropTypes.string,
}

export default React.memo(MapViewHealth);
